import React, { useState, useEffect, useMemo } from 'react';
import { Search, Palette } from 'lucide-react';
import './ColorCatalogueimg.css';
import colorData from './Color.json';

const ColorCatalogueimg = () => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const colorsPerPage = 48;

  // Process colors from Color.json
  const allColors = useMemo(() => {
    const colors = [];
    
    colorData.Sheet1.forEach((color, index) => {
      const name = color.Name?.trim();
      if (!name) return;
      
      const r = color['sR '] || color.sR || 0;
      const g = color['sG '] || color.sG || 0;
      const b = color['sB '] || color.sB || 0;
      const group1 = color['Group1: '] || color.Group1;
      
      const hex = `#${(((r) << 16) | ((g) << 8) | (b)).toString(16).padStart(6, '0')}`;
      
      colors.push({
        id: index + 1,
        name: name,
        r: r,
        g: g,
        b: b,
        hex: hex,
        group1: group1,
        group2: color['Group2: '] || color.Group2
      });
    });
    
    return colors;
  }, []);

  // Set initial color
  useEffect(() => {
    if (allColors.length > 0 && !selectedColor) {
      setSelectedColor(allColors[0]);
    }
  }, [allColors, selectedColor]);

  // Filter and search colors by name and color code
  const filteredColors = useMemo(() => {
    let filtered = allColors;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchLower) ||
        `at${c.group1}`.toLowerCase().includes(searchLower) ||
        `at - ${c.group1}`.toLowerCase().includes(searchLower) ||
        c.group1?.toString().includes(searchTerm)
      );
    }

    return filtered;
  }, [allColors, searchTerm]);

  // Paginate colors
  const paginatedColors = useMemo(() => {
    const startIndex = (currentPage - 1) * colorsPerPage;
    const endIndex = startIndex + colorsPerPage;
    return filteredColors.slice(startIndex, endIndex);
  }, [filteredColors, currentPage]);

  const totalPages = Math.ceil(filteredColors.length / colorsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!selectedColor) return (
    <div className="loading-screen">Loading colors...</div>
  );

  return (
    <div className="colorcatalogueimg-container">
      <div className="colorcatalogueimg-main">
        <div className="colorcatalogueimg-catalogue-wrapper">
          <div className="colorcatalogueimg-controls-bar">
            <div className="colorcatalogueimg-search-box">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search by color name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="colorcatalogueimg-search-input"
              />
            </div>
          </div>

          <div className="colorcatalogueimg-colors-container">
            {paginatedColors.length > 0 ? (
              <div className="colorcatalogueimg-colors-grid">
                {paginatedColors.map((color) => (
                  <button
                    key={color.id}
                    className={`colorcatalogueimg-color-card ${selectedColor?.id === color.id ? 'selected' : ''}`}
                    onClick={() => handleColorSelect(color)}
                  >
                    <div className="color-card-swatch" style={{ backgroundColor: color.hex }} />
                    <div className="color-card-info">
                      <h4 className="color-name">{color.name}</h4>
                      <p className="color-code">AT - {color.group1}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="colorcatalogueimg-empty-state">
                <Palette size={48} />
                <p>No colors found matching your search</p>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="colorcatalogueimg-pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              
              <div className="pagination-pages">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorCatalogueimg;